// Bomb the Hill's roadside (#211): the buildings, street lamps and trees
// along both sides of the road, laid out a chunk at a time in course z
// like the course itself, so they move with the road (#195) and leave a
// clear gap wherever a side street (#197) crosses. Sim- and three.js-free:
// `render/hill-course.ts` draws it with the scenery builders.
//
// The pattern is the one the hill's map file used to place by hand: a lamp
// every 30 m and a tree every 16 m just past each wall, and a row of
// buildings every 22 m further out, the two sides out of step.

import { CHUNK, type HillCrossing, type HillJunction } from "./hill-course";
import { SHOPFRONT } from "./hill-walk";

export type DressingKind = "building" | "lamp" | "tree";

/** Something on the roadside, at (x, z) in course z, its footprint `hw` either side across (x) and `hl` along the road (z). */
export interface Dressing {
  kind: DressingKind;
  x: number;
  z: number;
  hw: number;
  hl: number;
  /** Which side of the road it's on, -1 or 1: it faces the road. */
  side: -1 | 1;
  /** A tree's scale; a building's height (m). */
  size: number;
  /** A building's paint. */
  color?: string;
}

/** The buildings' paints, in the order they come along a side. */
const BUILDING_COLORS = ["#c98b6b", "#d9c7a7", "#8fa3b8", "#b5795f", "#e0d2b8", "#9c8f86"];

/** One kind's row along one side: every `every` m from `first` m below the top, `x` out from the road's middle, cycling through `xs`. */
interface Row {
  kind: DressingKind;
  every: number;
  first: number;
  xs: readonly number[];
  hw: number;
  hl: number;
}

/** Each side's rows, their x on the left (-x) side; the right mirrors it. The footprints count what overhangs: a lamp's arm, a tree's leaves, a building's awnings. */
const ROWS: Readonly<Record<-1 | 1, readonly Row[]>> = {
  [-1]: [
    { kind: "lamp", every: 30, first: 4, xs: [6.7], hw: 0.6, hl: 0.25 },
    { kind: "tree", every: 16, first: 4, xs: [9, 10, 11], hw: 2.2, hl: 2.2 },
    { kind: "building", every: 22, first: 11, xs: [19, 21], hw: 6, hl: 6.2 },
  ],
  [1]: [
    { kind: "lamp", every: 30, first: 16, xs: [6.7], hw: 0.6, hl: 0.25 },
    { kind: "tree", every: 16, first: -2, xs: [9, 10, 11], hw: 2.2, hl: 2.2 },
    { kind: "building", every: 22, first: 2, xs: [19, 21], hw: 6, hl: 6.2 },
  ],
};

/** The roadside's sizes (m). */
export const ROADSIDE = {
  /** The pavement either side of a side street, kept clear along with it. */
  pavement: 2,
  /** The radius the kerb rounds each side street's corners off by. */
  corner: 1.5,
  /** How far up past the top the first chunk's roadside reaches. */
  above: 12,
} as const;

/** The stretch of road z either side of a crosswalk's middle (#202) that the roadside keeps clear of: its footpath, the pavement its people walk along and the shopfront they go into (`SHOPFRONT`), and a little room. */
export function crossingClear(c: HillCrossing): number {
  return c.hl + SHOPFRONT.reach + 0.5;
}

/** The stretch of road z either side of a side street's middle that the roadside keeps clear of: the street, its rounded corners and pavements. */
export function sideStreetClear(j: HillJunction): number {
  return j.hl + ROADSIDE.corner + ROADSIDE.pavement;
}

/**
 * The roadside along chunk `k` of a road whose top is `top`: everything
 * whose z falls in the chunk, less anything whose footprint reaches into a
 * side street of `junctions` (all of them, from any chunk), its rounded
 * corners or its pavements, or into a crosswalk's footpath and the alley
 * it leads to (#202, any of `crossings`).
 */
export function chunkDressing(top: number, k: number, junctions: readonly HillJunction[], crossings: readonly HillCrossing[] = []): Dressing[] {
  const from = top + k * CHUNK - (k === 0 ? ROADSIDE.above : 0);
  const to = top + (k + 1) * CHUNK;
  const out: Dressing[] = [];
  for (const side of [-1, 1] as const) {
    for (const row of ROWS[side]) {
      const i0 = Math.ceil((from - top - row.first) / row.every);
      for (let i = i0; ; i++) {
        const z = top + row.first + i * row.every;
        if (z >= to) break;
        if (junctions.some((j) => Math.abs(z - j.z) < sideStreetClear(j) + row.hl)) continue;
        if (crossings.some((c) => Math.abs(z - c.z) < crossingClear(c) + row.hl)) continue;
        const n = ((i % 6) + 6) % 6;
        const x = side * row.xs[n % row.xs.length]!;
        if (row.kind === "building") {
          const color = BUILDING_COLORS[(n + (side > 0 ? 3 : 0)) % BUILDING_COLORS.length]!;
          out.push({ kind: "building", x, z, hw: row.hw, hl: row.hl, side, size: [7, 9, 11][n % 3]!, color });
        } else out.push({ kind: row.kind, x, z, hw: row.hw, hl: row.hl, side, size: row.kind === "tree" ? [1, 1.15, 1.3][n % 3]! : 1 });
      }
    }
  }
  return out;
}
